import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Calendar, Pause, Play, Plus, Settings, Trash2, Wand2, X } from "lucide-react";
import { toast } from "sonner";

const WEEKDAY_LABELS = ["M","T","W","T","F","S","S"];
const STATUS_COLOR = {
  planned:    "bg-stone-300",
  rendering:  "bg-blue-500 animate-pulse",
  ready:      "bg-emerald-500",
  publishing: "bg-amber-500 animate-pulse",
  published:  "bg-slate-900",
  failed:     "bg-red-500",
  skipped:    "bg-stone-400 opacity-50",
};
const STATUS_LABEL = {
  planned: "planned", rendering: "rendering", ready: "ready",
  publishing: "publishing", published: "published",
  failed: "failed", skipped: "skipped",
};

function isoDate(d) { return d.toISOString().slice(0, 10); }
function startOfWeek(d) {
  const x = new Date(d);
  const wd = (x.getDay() + 6) % 7;  // Monday = 0
  x.setDate(x.getDate() - wd);
  x.setHours(0, 0, 0, 0);
  return x;
}

export default function Schedule() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [entries, setEntries] = useState([]);
  const [config, setConfig] = useState(null);
  const [pools, setPools] = useState(null);
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerEntry, setDrawerEntry] = useState(null);
  const [creatingForDay, setCreatingForDay] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const weekEnd = useMemo(() => {
    const x = new Date(weekStart); x.setDate(x.getDate() + 6); return x;
  }, [weekStart]);

  async function reload() {
    setLoading(true);
    try {
      const [e, c, p, w] = await Promise.all([
        api.get(`/schedule?from=${isoDate(weekStart)}&to=${isoDate(weekEnd)}`),
        api.get("/schedule/channel-config"),
        api.get("/schedule/topic-pools"),
        api.get("/schedule/worker-status"),
      ]);
      setEntries(e.data || []);
      setConfig(c.data);
      setPools(p.data);
      setWorker(w.data);
    } catch (err) {
      toast.error("Failed to load schedule");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [weekStart]);

  const summary = useMemo(() => {
    const s = { planned: 0, rendering: 0, ready: 0, publishing: 0,
                published: 0, failed: 0, skipped: 0 };
    entries.forEach((e) => { s[e.status] = (s[e.status] || 0) + 1; });
    return s;
  }, [entries]);

  function entriesFor(day) {
    const iso = isoDate(day);
    return entries.filter((e) => e.scheduled_date === iso);
  }

  async function createEntry(payload) {
    try {
      await api.post("/schedule", payload);
      toast.success("Added to schedule");
      setCreatingForDay(null);
      reload();
    } catch (e) {
      toast.error("Could not create entry");
    }
  }
  async function deleteEntry(id) {
    if (!window.confirm("Remove this scheduled entry?")) return;
    await api.delete(`/schedule/${id}`);
    setDrawerEntry(null);
    reload();
  }
  async function patchEntry(id, patch) {
    const { data } = await api.patch(`/schedule/${id}`, patch);
    setDrawerEntry(data);
    reload();
  }
  async function populate() {
    try {
      const r = await api.post("/schedule/populate-week",
                                null, { params: { start: isoDate(weekStart) } });
      toast.success(`Populated ${r.data.created} entries`);
      reload();
    } catch {
      toast.error("Populate failed");
    }
  }
  async function toggleWorker() {
    const path = worker?.paused ? "worker/resume" : "worker/pause";
    await api.post(`/schedule/${path}`);
    reload();
  }

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart); d.setDate(d.getDate() + i);
    days.push(d);
  }

  return (
    <div className="px-5 py-6 max-w-[1380px] mx-auto" data-testid="schedule-page">
      <header className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2">
            <Calendar className="text-amber-500" /> Schedule
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {isoDate(weekStart)} → {isoDate(weekEnd)} · {config?.tz}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => {
            const x = new Date(weekStart); x.setDate(x.getDate() - 7); setWeekStart(x);
          }}
                  className="px-3 py-1.5 rounded-lg bg-white border text-sm hover:bg-stone-50"
                  data-testid="schedule-prev-week">← Prev</button>
          <button onClick={() => setWeekStart(startOfWeek(new Date()))}
                  className="px-3 py-1.5 rounded-lg bg-stone-900 text-white text-sm"
                  data-testid="schedule-this-week">This week</button>
          <button onClick={() => {
            const x = new Date(weekStart); x.setDate(x.getDate() + 7); setWeekStart(x);
          }}
                  className="px-3 py-1.5 rounded-lg bg-white border text-sm hover:bg-stone-50"
                  data-testid="schedule-next-week">Next →</button>
          <button onClick={populate}
                  className="px-3 py-1.5 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white text-sm font-semibold flex items-center gap-1.5"
                  data-testid="schedule-populate-btn">
            <Wand2 size={14}/> Auto-fill week
          </button>
          <button onClick={() => setShowSettings(true)}
                  className="p-2 rounded-lg bg-white border hover:bg-stone-50"
                  aria-label="Channel config"
                  data-testid="schedule-config-btn">
            <Settings size={16} />
          </button>
        </div>
      </header>

      {/* Summary banner */}
      <div className="mb-4 flex flex-wrap gap-3 items-center text-sm"
           data-testid="schedule-summary">
        {Object.entries(summary).filter(([_,v]) => v > 0).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border">
            <span className={`inline-block w-2 h-2 rounded-full ${STATUS_COLOR[k]}`} />
            <span className="font-semibold">{v}</span>
            <span className="text-stone-500">{STATUS_LABEL[k]}</span>
          </span>
        ))}
        <span className="ml-auto flex items-center gap-2 px-3 py-1 rounded-full bg-white border"
              data-testid="schedule-worker-chip">
          <span className={`inline-block w-2 h-2 rounded-full ${worker?.paused ? "bg-stone-400" : "bg-emerald-500 animate-pulse"}`} />
          worker {worker?.paused ? "paused" : "running"}
          <button onClick={toggleWorker}
                  className="ml-2 text-xs font-semibold text-amber-700 hover:underline"
                  data-testid="schedule-worker-toggle">
            {worker?.paused ? <><Play size={11} className="inline"/> resume</>
                            : <><Pause size={11} className="inline"/> pause</>}
          </button>
        </span>
      </div>

      {/* Week grid */}
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-3" data-testid="schedule-week-grid">
        {days.map((d, i) => {
          const dayEntries = entriesFor(d);
          const wd = d.getDay() === 0 ? 7 : d.getDay();  // ISO
          const isMusicDay = (config?.music_days || []).includes(wd);
          const isShortDay = (config?.shorts_days || []).includes(wd);
          const isToday = isoDate(d) === isoDate(new Date());
          return (
            <div key={i}
                 className={"rounded-2xl border p-3 min-h-[190px] flex flex-col gap-2 bg-white " +
                            (isToday ? "ring-2 ring-amber-400" : "")}
                 data-testid={`schedule-day-${isoDate(d)}`}>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-stone-500">
                    {WEEKDAY_LABELS[i]}
                  </div>
                  <div className="text-xl font-black">{d.getDate()}</div>
                </div>
                <div className="flex gap-1 text-[10px] font-bold">
                  {isMusicDay && <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">M</span>}
                  {isShortDay && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">S</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 flex-1">
                {dayEntries.map((e) => (
                  <button key={e.id}
                          onClick={() => setDrawerEntry(e)}
                          className={"text-left rounded-lg px-2.5 py-2 border shadow-sm hover:shadow transition " +
                                     (e.content_type === "music"
                                       ? "bg-indigo-50 border-indigo-200 text-indigo-900"
                                       : "bg-amber-50 border-amber-200 text-amber-900")}
                          data-testid={`schedule-entry-${e.id}`}>
                    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${STATUS_COLOR[e.status]}`} />
                      {e.content_type} · {e.scheduled_time}
                    </div>
                    <div className="text-sm font-semibold line-clamp-2 mt-0.5">
                      {e.topic_label || e.topic}
                    </div>
                  </button>
                ))}
                <button onClick={() => setCreatingForDay(isoDate(d))}
                        className="mt-auto text-xs text-stone-500 hover:text-stone-900 border border-dashed rounded-lg px-2.5 py-1.5 flex items-center justify-center gap-1 hover:bg-stone-50"
                        data-testid={`schedule-add-${isoDate(d)}`}>
                  <Plus size={12}/> Add content
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {loading && <div className="mt-4 text-sm text-stone-400">Loading…</div>}

      {/* Drawer */}
      {drawerEntry && (
        <EditDrawer
          entry={drawerEntry} onClose={() => setDrawerEntry(null)}
          onDelete={() => deleteEntry(drawerEntry.id)}
          onSave={(patch) => patchEntry(drawerEntry.id, patch)}
        />
      )}
      {creatingForDay && (
        <CreateDialog day={creatingForDay} config={config} pools={pools}
                      onClose={() => setCreatingForDay(null)}
                      onCreate={createEntry} />
      )}
      {showSettings && config && (
        <ConfigDialog config={config}
                      onClose={() => setShowSettings(false)}
                      onSave={async (patch) => {
                        await api.patch("/schedule/channel-config", patch);
                        toast.success("Settings saved");
                        setShowSettings(false);
                        reload();
                      }} />
      )}
    </div>
  );
}

// ---------- Edit drawer ----------
function EditDrawer({ entry, onClose, onDelete, onSave }) {
  const [topic, setTopic] = useState(entry.topic || "");
  const [time, setTime] = useState(entry.scheduled_time || "08:00");
  const [vis, setVis] = useState(entry.visibility || "unlisted");
  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="schedule-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full sm:w-[420px] h-full bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-bold text-lg">Edit entry</h3>
          <button onClick={onClose} aria-label="Close"
                  data-testid="schedule-drawer-close"><X /></button>
        </div>
        <div className="p-5 flex flex-col gap-4 overflow-auto">
          <div className="text-xs text-stone-500">{entry.scheduled_date}</div>
          <Field label="Topic">
            <input value={topic} onChange={(e) => setTopic(e.target.value)}
                   className="w-full rounded-lg border px-3 py-2"
                   data-testid="schedule-edit-topic" />
          </Field>
          <Field label="Time">
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                   className="w-full rounded-lg border px-3 py-2" />
          </Field>
          <Field label="Visibility">
            <select value={vis} onChange={(e) => setVis(e.target.value)}
                    className="w-full rounded-lg border px-3 py-2">
              {["unlisted","public","private"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <div className="text-xs text-stone-500">
            status: <span className="font-mono">{entry.status}</span>
            {entry.youtube_url && <> · <a className="text-amber-700 underline"
                                           href={entry.youtube_url} target="_blank" rel="noreferrer">YouTube</a></>}
            {entry.last_error && <div className="mt-1 text-red-600">{entry.last_error}</div>}
          </div>
        </div>
        <div className="p-5 border-t flex items-center justify-between gap-2">
          <button onClick={onDelete}
                  className="p-2 rounded-lg border text-red-600 hover:bg-red-50"
                  aria-label="Delete"
                  data-testid="schedule-drawer-delete"><Trash2 size={16}/></button>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
                    className="px-3 py-2 rounded-lg border text-sm">Cancel</button>
            <button onClick={() => onSave({ topic, scheduled_time: time, visibility: vis })}
                    className="px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold"
                    data-testid="schedule-drawer-save">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Create dialog ----------
function CreateDialog({ day, config, pools, onClose, onCreate }) {
  const d = new Date(day);
  const wd = d.getDay() === 0 ? 7 : d.getDay();
  const defaultType = (config?.music_days || []).includes(wd) ? "music" : "short";
  const [type, setType] = useState(defaultType);
  const [topic, setTopic] = useState("");
  const [time, setTime] = useState(
    defaultType === "music" ? (config?.music_publish_time || "07:00")
                            : (config?.shorts_publish_time || "12:00")
  );
  const [vis, setVis] = useState("unlisted");
  const pool = type === "music" ? (pools?.music_topic_pool || [])
                                : (pools?.shorts_topic_pool || []);
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4"
         data-testid="schedule-create-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-bold text-lg">Add to {day}</h3>
            <p className="text-xs text-stone-500">Plan a render — worker will pick it up if resumed.</p>
          </div>
          <button onClick={onClose} aria-label="Close"><X /></button>
        </div>
        <div className="flex gap-2">
          {["music","short"].map((t) => (
            <button key={t} onClick={() => {
              setType(t);
              setTime(t === "music" ? (config?.music_publish_time || "07:00")
                                    : (config?.shorts_publish_time || "12:00"));
            }}
                    className={"flex-1 rounded-lg border py-2 text-sm font-semibold " +
                               (type === t ? (t === "music" ? "bg-indigo-50 border-indigo-400 text-indigo-800"
                                                            : "bg-amber-50 border-amber-400 text-amber-800")
                                           : "bg-white")}>
              {t === "music" ? "Music video" : "Short"}
            </button>
          ))}
        </div>
        <Field label="Topic">
          <input list="topic-pool" value={topic}
                 onChange={(e) => setTopic(e.target.value)}
                 placeholder="e.g. Itsy Bitsy Spider"
                 className="w-full rounded-lg border px-3 py-2"
                 data-testid="schedule-new-topic"/>
          <datalist id="topic-pool">
            {pool.map((t) => <option key={t} value={t}/>)}
          </datalist>
        </Field>
        <div className="flex gap-3">
          <Field label="Time">
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                   className="w-full rounded-lg border px-3 py-2" />
          </Field>
          <Field label="Visibility">
            <select value={vis} onChange={(e) => setVis(e.target.value)}
                    className="w-full rounded-lg border px-3 py-2">
              {["unlisted","public","private"].map((v) => <option key={v}>{v}</option>)}
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose}
                  className="px-3 py-2 rounded-lg border text-sm">Cancel</button>
          <button disabled={!topic.trim()}
                  onClick={() => onCreate({
                    scheduled_date: day, scheduled_time: time,
                    content_type: type, topic: topic.trim(), visibility: vis,
                  })}
                  className="px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold disabled:opacity-50"
                  data-testid="schedule-new-create">Create</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Config dialog ----------
function ConfigDialog({ config, onClose, onSave }) {
  const [tz, setTz] = useState(config.tz);
  const [musicTime, setMusicTime] = useState(config.music_publish_time);
  const [shortTime, setShortTime] = useState(config.shorts_publish_time);
  const [musicDays, setMusicDays] = useState(new Set(config.music_days || []));
  const [shortsDays, setShortsDays] = useState(new Set(config.shorts_days || []));
  function tog(set, setSet, wd) {
    const n = new Set(set);
    n.has(wd) ? n.delete(wd) : n.add(wd);
    setSet(n);
  }
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4"
         data-testid="schedule-config-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <h3 className="font-bold text-lg">Channel schedule settings</h3>
          <button onClick={onClose} aria-label="Close"><X/></button>
        </div>
        <Field label="Timezone">
          <input value={tz} onChange={(e) => setTz(e.target.value)}
                 className="w-full rounded-lg border px-3 py-2"
                 data-testid="schedule-config-tz" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Music publish time">
            <input type="time" value={musicTime}
                   onChange={(e) => setMusicTime(e.target.value)}
                   className="w-full rounded-lg border px-3 py-2" />
          </Field>
          <Field label="Shorts publish time">
            <input type="time" value={shortTime}
                   onChange={(e) => setShortTime(e.target.value)}
                   className="w-full rounded-lg border px-3 py-2" />
          </Field>
        </div>
        <Field label="Music days">
          <div className="flex gap-1">
            {WEEKDAY_LABELS.map((lbl, i) => {
              const wd = i + 1;
              const on = musicDays.has(wd);
              return (
                <button key={wd} type="button"
                        onClick={() => tog(musicDays, setMusicDays, wd)}
                        className={"w-9 h-9 rounded-lg text-xs font-bold border " +
                                   (on ? "bg-indigo-500 text-white border-indigo-500"
                                       : "bg-white")}>{lbl}</button>
              );
            })}
          </div>
        </Field>
        <Field label="Shorts days">
          <div className="flex gap-1">
            {WEEKDAY_LABELS.map((lbl, i) => {
              const wd = i + 1;
              const on = shortsDays.has(wd);
              return (
                <button key={wd} type="button"
                        onClick={() => tog(shortsDays, setShortsDays, wd)}
                        className={"w-9 h-9 rounded-lg text-xs font-bold border " +
                                   (on ? "bg-amber-500 text-white border-amber-500"
                                       : "bg-white")}>{lbl}</button>
              );
            })}
          </div>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose}
                  className="px-3 py-2 rounded-lg border text-sm">Cancel</button>
          <button onClick={() => onSave({
            tz, music_publish_time: musicTime, shorts_publish_time: shortTime,
            music_days: Array.from(musicDays).sort(),
            shorts_days: Array.from(shortsDays).sort(),
          })}
                  className="px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold"
                  data-testid="schedule-config-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-stone-600 font-medium">{label}</span>
      {children}
    </label>
  );
}
